import axios from "axios";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Home, IndianRupee, ArrowUpRight, Loader2, MapPin, MessageCircle } from "lucide-react";
import { PROPERTIES_FOR_SALE, CONTACT } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = CONTACT.whatsapp.match(/wa\.me\/(\d+)/)[1];

const inputClass =
  "w-full bg-white border border-slate-300 px-4 py-3.5 text-sm text-prussian placeholder:text-slate-400 focus:outline-none focus:border-blueprint-accent focus:ring-1 focus:ring-blueprint-accent transition-[border-color,box-shadow] duration-300";

const RealEstate = () => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setFocus,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { intent: "Buying" } });
  const intent = watch("intent");

  const onSubmit = async (data) => {
    try {
      await axios.post(`${API}/enquiries`, {
        name: data.name,
        phone: data.phone,
        service: `Real Estate — ${data.intent}`,
        message: data.details || undefined,
      });
      toast.success("Enquiry received. Our real estate desk will call you back within 24 hours.");
      reset({ intent: data.intent });
    } catch {
      toast.error("Could not send your enquiry. Please call or WhatsApp us directly.");
    }
  };

  const listingSummary = (listing) =>
    `${listing.title} — ${listing.size}, ${listing.facing}${listing.price ? `, ${listing.price}` : ""}, ${listing.address}`;

  const listingWhatsAppUrl = (listing) =>
    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      `Hello Sai Mohan Constructions, I'm interested in your listing:\n${listingSummary(listing)}\nLandmark: ${listing.landmark}\n\nPlease share more details.`
    )}`;

  const enquireListing = (listing) => {
    setValue("intent", "Buying");
    setValue("details", `Interested in: ${listingSummary(listing)}`);
    setFocus("name");
  };

  const PathCard = ({ value, icon: Icon, title, body, testid }) => {
    const active = intent === value;
    return (
      <button
        type="button"
        onClick={() => setValue("intent", value)}
        data-testid={testid}
        className={`text-left border p-6 transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 ${
          active ? "border-blueprint-accent bg-white/10" : "border-white/15 hover:border-white/35"
        }`}
      >
        <Icon size={24} strokeWidth={1.5} className={active ? "text-[#4D86FF]" : "text-blueprint-line"} />
        <span className="block font-display font-bold tracking-tight text-lg text-white mt-4">{title}</span>
        <span className="block text-sm text-blueprint-line/70 leading-relaxed mt-2">{body}</span>
        {active && (
          <span className="block font-mono text-[9px] tracking-[0.25em] uppercase text-[#4D86FF] mt-4">
            Selected
          </span>
        )}
      </button>
    );
  };

  return (
    <section
      id="real-estate"
      data-testid="realestate-section"
      className="relative bg-prussian"
      style={{
        backgroundImage:
          "linear-gradient(rgba(191,219,254,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(191,219,254,0.06) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
      }}
    >
      <span className="absolute top-3 left-4 font-mono text-blueprint-line/40 select-none" aria-hidden>+</span>
      <span className="absolute bottom-3 right-4 font-mono text-blueprint-line/40 select-none" aria-hidden>+</span>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
        {PROPERTIES_FOR_SALE.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mb-20 md:mb-28"
            data-testid="realestate-listings"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="relative flex h-2 w-2" aria-hidden>
                <span className="animate-ping absolute inline-flex h-full w-full bg-[#C62828] opacity-75" />
                <span className="relative inline-flex h-2 w-2 bg-[#C62828]" />
              </span>
              <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#4D86FF]">
                Available Now — Featured Listing
              </p>
              <span className="flex-1 h-px bg-white/15" aria-hidden />
            </div>
            <div className="space-y-5">
              {PROPERTIES_FOR_SALE.map((l, i) => (
                <article
                  key={`${l.title}-${l.address}`}
                  data-testid={`realestate-listing-${i + 1}`}
                  className="relative bg-white border border-blueprint-line shadow-[8px_8px_0px_#4D86FF] grid md:grid-cols-12"
                >
                  <span className="absolute -top-px -left-px font-mono text-blueprint/60 bg-white px-1 select-none" aria-hidden>+</span>
                  <span className="absolute -top-px -right-px font-mono text-blueprint/60 bg-white px-1 select-none" aria-hidden>+</span>
                  <div className="md:col-span-5 p-7 md:p-9 border-b md:border-b-0 md:border-r border-blueprint-line">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-white bg-[#C62828] px-2 py-1">
                        For Sale
                      </span>
                      <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-blueprint-accent border border-blueprint-line px-2 py-1">
                        {l.facing}
                      </span>
                    </div>
                    <h3 className="font-display font-extrabold tracking-tight leading-[1.02] text-3xl md:text-4xl text-prussian mt-5">
                      {l.title}
                      <span className="block text-blueprint-accent">{l.size}</span>
                    </h3>
                    {l.price && (
                      <p className="mt-5 font-mono text-base tracking-[0.05em] text-prussian">
                        {l.price}
                        {l.total && (
                          <span className="block text-[10px] tracking-[0.2em] uppercase text-slate-400 mt-1">
                            {l.total}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-7 p-7 md:p-9 flex flex-col">
                    <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-slate-400">
                      Listing / 0{i + 1} — Location
                    </p>
                    <p className="mt-4 flex items-start gap-2 text-base text-prussian leading-relaxed">
                      <MapPin size={17} className="text-blueprint-accent shrink-0 mt-1" />
                      {l.address}
                    </p>
                    <p className="mt-2 pl-[25px] text-sm text-slate-500">Landmark: {l.landmark}</p>
                    <div className="mt-auto pt-7">
                      <a
                        href={listingWhatsAppUrl(l)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => enquireListing(l)}
                        data-testid={`realestate-listing-enquire-${i + 1}`}
                        className="inline-flex items-center gap-2 bg-[#C62828] text-white font-mono text-xs tracking-[0.2em] uppercase px-7 py-4 hover:bg-prussian transition-colors duration-300"
                      >
                        <MessageCircle size={15} /> Enquire on WhatsApp <ArrowUpRight size={15} />
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#4D86FF] mb-6">
            Real Estate Desk
          </p>
          <h2 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-6xl text-white">
            Buy. Sell.<br />Invest.
          </h2>
          <p className="mt-6 text-sm md:text-base text-blueprint-line/80 leading-relaxed max-w-md">
            Beyond building, we deal in properties — plots, flats, villas and
            commercial spaces across Hyderabad. Whether you are buying your first
            property or selling one, tell us below and our desk will reach out.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <PathCard
              value="Buying"
              icon={Home}
              title="I want to Buy"
              body="Plots, flats, villas and commercial spaces — verified titles, fair prices."
              testid="realestate-buy-card"
            />
            <PathCard
              value="Selling"
              icon={IndianRupee}
              title="I want to Sell"
              body="Fair valuation, genuine buyers and a hassle-free sale process."
              testid="realestate-sell-card"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="realestate-enquiry-form"
            className="bg-white border border-blueprint-line p-7 md:p-10 relative"
          >
            <span className="absolute -top-px -left-px font-mono text-blueprint/60 bg-white px-1 select-none" aria-hidden>+</span>
            <span className="absolute -top-px -right-px font-mono text-blueprint/60 bg-white px-1 select-none" aria-hidden>+</span>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400 mb-8">
              Form / RE-02 — Real Estate Enquiry
            </p>

            <div className="space-y-6">
              <div>
                <label htmlFor="re-name" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                  Your Name *
                </label>
                <input
                  id="re-name"
                  data-testid="realestate-form-name-input"
                  placeholder="e.g. Ramesh Kumar"
                  className={inputClass}
                  {...register("name", { required: true, minLength: 2 })}
                />
                {errors.name && <p data-testid="realestate-form-name-error" className="mt-1.5 text-xs text-red-600">Please enter your name.</p>}
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="re-phone" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                    Phone *
                  </label>
                  <input
                    id="re-phone"
                    data-testid="realestate-form-phone-input"
                    placeholder="+91 ..."
                    className={inputClass}
                    {...register("phone", { required: true, minLength: 8 })}
                  />
                  {errors.phone && <p data-testid="realestate-form-phone-error" className="mt-1.5 text-xs text-red-600">Valid phone required.</p>}
                </div>
                <div>
                  <label htmlFor="re-intent" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                    I am *
                  </label>
                  <select
                    id="re-intent"
                    data-testid="realestate-form-intent-select"
                    className={inputClass}
                    {...register("intent", { required: true })}
                  >
                    <option value="Buying">Buying a property</option>
                    <option value="Selling">Selling a property</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="re-details" className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
                  Details
                </label>
                <textarea
                  id="re-details"
                  rows={4}
                  data-testid="realestate-form-details-textarea"
                  placeholder="Location, budget, plot/flat size, timeline..."
                  className={`${inputClass} resize-none`}
                  {...register("details")}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="realestate-form-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C62828] text-white font-mono text-xs tracking-[0.2em] uppercase px-7 py-4 hover:bg-prussian disabled:opacity-60 transition-colors duration-300"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={15} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Enquiry <ArrowUpRight size={15} />
                  </>
                )}
              </button>
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-slate-400 text-center">
                Confidential — your details are never shared
              </p>
            </div>
          </form>
        </motion.div>
        </div>
      </div>
    </section>
  );
};

export default RealEstate;
